import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import "./stylesFreeSpinsModal.css";

import { CloseOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import { usePamFeature } from "../../../../../hooks/useFeatureFlag";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { Button, Form, Input, message, Select } from "antd";
import CustomAutoComplete from "../../../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";

const PROVIDER_OPTIONS = [
  { label: AppStrings.BGAMING, value: "bgaming" },
  { label: AppStrings.THREEOAKS, value: "threeoaks" },
];

const requiredRule = (fieldLabel) => ({
  required: true,
  message: AppStrings.FIELD_IS_REQUIRED.replace("{title}", fieldLabel),
});

const gameIdRules = [
  {
    validator: (_, v) => {
      if (v === undefined || v === null || v === "") {
        return Promise.reject(new Error(AppStrings.SELECT_GAME));
      }
      return Promise.resolve();
    },
  },
];

const positiveQuantityRules = (fieldLabel) => [
  requiredRule(fieldLabel),
  {
    validator: (_, val) => {
      const n = parseInt(val, 10);
      if (
        val === "" ||
        val === undefined ||
        val === null ||
        Number.isNaN(n) ||
        n <= 0
      ) {
        return Promise.reject(new Error(AppStrings.INVALID_VALUE));
      }
      return Promise.resolve();
    },
  },
];

const threeOaksValueRules = [
  requiredRule(AppStrings.VALUE),
  {
    validator: (_, val) => {
      const n = parseFloat(val);
      if (!val || Number.isNaN(n) || n <= 0) {
        return Promise.reject(new Error(AppStrings.VALUE_REQUIRED_FOR_THREEOAKS));
      }
      return Promise.resolve();
    },
  },
];

function FreeSpinsGameField({
  value,
  onChange,
  allOptions,
  disabled,
  isLoading,
}) {
  if (isLoading) {
    return (
      <div className="loading-games">
        <RebetLoader height={"30px"} width={"30px"} />
      </div>
    );
  }
  return (
    <CustomAutoComplete
      allOptions={allOptions}
      value={value}
      handleChange={(gameId) => onChange?.(gameId)}
      bgColor="#25252f"
      clearOptions={() => onChange?.("")}
      selector="game_id"
      disabled={disabled}
      virtual={false}
    />
  );
}

const FreeSpinsModal = forwardRef(({ handleCancel, data }, ref) => {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const [form] = Form.useForm();
  const { isEnabled: useBonusV2, loading: flagLoading } = usePamFeature("freeSpinsIssuance");

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [availableGames, setAvailableGames] = useState({
    bgaming: [],
    threeoaks: [],
  });

  const provider = Form.useWatch("provider", form) ?? "bgaming";
  const gameId = Form.useWatch("game_id", form);

  const selectedGame = useMemo(
    () =>
      availableGames[provider]?.find((g) => g.game_id === gameId) ?? null,
    [availableGames, provider, gameId],
  );

  const gameOptions = useMemo(
    () =>
      (availableGames[provider] || []).map((game) => ({
        label: game.game_name,
        value: game.game_name,
        game_id: game.game_id,
      })),
    [availableGames, provider],
  );

  useEffect(() => {
    const fetchGames = async () => {
      setIsLoadingGames(true);
      try {
        const requests = [
          PrivateAdminPanelInstance?.get(
            `${API_ROUTES.GET_FREE_SPINS_GAMES}?provider=bgaming`,
          ),
          PrivateAdminPanelInstance?.get(
            `${API_ROUTES.GET_FREE_SPINS_GAMES}?provider=threeoaks`,
          ),
        ];

        const responses = await Promise.allSettled(requests);

        const [bgamingResponse, threeoaksResponse] = responses;

        if (bgamingResponse?.status === "fulfilled") {
          setAvailableGames((prev) => ({
            ...prev,
            bgaming: bgamingResponse.value?.data?.data || [],
          }));
        } else {
          setAvailableGames((prev) => ({
            ...prev,
            bgaming: [],
          }));
        }

        if (threeoaksResponse?.status === "fulfilled") {
          setAvailableGames((prev) => ({
            ...prev,
            threeoaks: threeoaksResponse.value?.data?.data || [],
          }));
        } else {
          setAvailableGames((prev) => ({
            ...prev,
            threeoaks: [],
          }));
        }
      } catch (error) {
        console.error("Error fetching free spins games:", error);
        message.error(AppStrings.ERROR_FETCHING_GAMES);
        setAvailableGames((prev) => ({
          ...prev,
          bgaming: [],
          threeoaks: [],
        }));
      } finally {
        setIsLoadingGames(false);
      }
    };

    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearForm = () => {
    form.resetFields();
    setIsApiRunning(false);
  };

  const handleProviderChange = () => {
    form.setFieldsValue({
      game_id: undefined,
      freespins_quantity: undefined,
      value: undefined,
      period_days: undefined,
      ...(useBonusV2 ? {} : { reason: "" }),
    });
  };

  const buildBonusV2Payload = (values) => {
    const base = {
      user_id: data.user_id,
      currency: selectedGame?.currency,
      game: values.game_id,
      period_days: parseInt(values.period_days, 10),
      provider: values.provider,
    };

    if (values.provider === "bgaming") {
      return {
        ...base,
        data: {
          amount: parseInt(values.freespins_quantity, 10),
          level: 1,
        },
      };
    }

    if (values.provider === "threeoaks") {
      return {
        ...base,
        data: {
          // Bonus v2 expects minor units (cents); value field is in dollars.
          amount: Math.round(parseFloat(values.value) * 100),
          rounds: 1,
          type: "fixed",
        },
      };
    }

    return base;
  };

  const handleFinish = async (values) => {
    if (!data?.user_id) return;
    if (flagLoading) return;
    if (useBonusV2 && !selectedGame) return;

    setIsApiRunning(true);

    try {
      if (useBonusV2) {
        const payload = buildBonusV2Payload(values);
        await PrivateAdminPanelInstance?.post(API_ROUTES.ISSUE_FREE_SPINS_V2, payload);
        message.success(AppStrings.FREE_SPINS_QUEUED);
      } else {
        const trimmedReason = (values.reason || "").trim();
        const params = {
          provider: values.provider,
          game_id: values.game_id,
          freespins_quantity: parseInt(values.freespins_quantity, 10),
          period_days: parseInt(values.period_days, 10),
          reason: trimmedReason,
        };
        if (values.provider === "threeoaks") {
          params.value = parseFloat(values.value);
          params.campaign = "pam_admin_panel";
        }
        const apiRoute = API_ROUTES.GIVE_USER_FREE_SPINS.replace("{user_id}", data.user_id);
        await PrivateAdminPanelInstance?.post(apiRoute, params);
        message.success(AppStrings.FREE_SPINS_ADDED_SUCCESSFULLY);
      }
      clearForm();
      handleCancel();
    } catch (error) {
      console.error("Error in setting free spins:", error);

      if (useBonusV2) {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          AppStrings.ERROR_PROCESSING_REQUEST;
        message.error(errorMessage);
      } else {
        const errorCode = error?.response?.data?.error_code;
        switch (errorCode) {
          case "INVALID_FREE_SPINS_GAME":
            message.error(AppStrings.ERROR_GAME_NOT_IN_ALLOWED_LIST);
            break;
          case "INVALID_BGAMING_VALUE":
            message.error(AppStrings.ERROR_VALUE_NOT_FOR_BGAMING);
            break;
          case "MISSING_THREEOAKS_VALUE":
            message.error(AppStrings.ERROR_VALUE_REQUIRED_THREEOAKS);
            break;
          case "INVALID_THREEOAKS_BET_FACTOR":
            message.error(AppStrings.ERROR_BET_FACTOR_MISMATCH);
            break;
          case "FREE_SPINS_PROVIDER_ERROR":
            message.error(AppStrings.ERROR_PROVIDER_FAILED);
            break;
          default:
            message.error(
              error?.response?.data?.message || AppStrings.ERROR_PROCESSING_REQUEST,
            );
        }
      }
    } finally {
      setIsApiRunning(false);
    }
  };

  useImperativeHandle(ref, () => ({
    clearForm,
  }));

  const isThreeOaks = provider === "threeoaks";

  return (
    <div className="free-bet-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title"> {AppStrings.FREE_SPINS}</h2>
        </div>
        <CloseOutlined onClick={handleCancel} className="close-icon" />
      </div>

      <div className="free-bets-body">
        <Form
          form={form}
          layout="vertical"
          className="free-spins-form"
          onFinish={handleFinish}
          requiredMark={false}
          initialValues={{
            provider: "bgaming",
            game_id: undefined,
            freespins_quantity: undefined,
            value: undefined,
            period_days: undefined,
            ...(useBonusV2 ? {} : { reason: "" }),
          }}
        >
          <Form.Item
            className="form-group"
            label={`${AppStrings.PROVIDER}:`}
            name="provider"
            rules={[requiredRule(AppStrings.PROVIDER)]}
          >
            <Select
              className="provider-select"
              options={PROVIDER_OPTIONS}
              onChange={handleProviderChange}
            />
          </Form.Item>

           <Form.Item
            className="form-group"
            label={`${AppStrings.CASINO_GAME}:`}
            name="game_id"
            rules={gameIdRules}
          >
            <FreeSpinsGameField
              allOptions={gameOptions}
              disabled={!provider}
              isLoading={isLoadingGames}
            />
          </Form.Item>

          <Form.Item
            className="form-group"
            label={`${AppStrings.FREE_SPINS}:`}
            name="freespins_quantity"
            rules={positiveQuantityRules(AppStrings.FREE_SPINS)}
          >
            <Input
              type="number"
              min={1}
              name="freespins_quantity"
              placeholder="#"
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>

          {isThreeOaks && (
            <Form.Item
              className="form-group"
              label={`${AppStrings.VALUE}:`}
              name="value"
              rules={threeOaksValueRules}
              extra={
                selectedGame?.bet_factor ? (
                  <span
                    className="bet-factor-hint"
                    data-testid="free-spins-bet-factor-hint"
                  >
                    {AppStrings.BET_FACTOR}: {selectedGame.bet_factor}
                  </span>
                ) : null
              }
            >
              <Input
                type="number"
                min={0}
                step={0.01}
                name="value"
                placeholder="$"
                onWheel={(e) => e.target.blur()}
              />
            </Form.Item>
          )}

          <Form.Item
            className="form-group"
            label={`${AppStrings.PERIOD}:`}
            name="period_days"
            rules={positiveQuantityRules(AppStrings.PERIOD)}
          >
            <Input
              type="number"
              min={1}
              name="period_days"
              placeholder={AppStrings.DAYS}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>

          {!flagLoading && !useBonusV2 && (
            <Form.Item
              className="form-group"
              label={`${AppStrings.REASON}:`}
              name="reason"
              rules={[
                requiredRule(AppStrings.REASON),
                {
                  whitespace: true,
                  message: AppStrings.FIELD_IS_REQUIRED.replace(
                    "{title}",
                    AppStrings.REASON,
                  ),
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                name="reason"
                placeholder={AppStrings.ENTER_REASON_PLACEHOLDER}
              />
            </Form.Item>
          )}

          <Form.Item className="free-spins-form-actions">
            <Button
              type="primary"
              htmlType="submit"
              className={`free-spins-submit-btn ${isApiRunning ? "button-sending" : ""}`}
              disabled={isApiRunning || isLoadingGames || flagLoading}
            >
              {isApiRunning ? (
                <RebetLoader height={"50px"} width={"50px"} />
              ) : (
                AppStrings.ADD_FREE_SPINS
              )}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
});

export default FreeSpinsModal;
